import { emailRegexp, phoneRegexp } from "../../regexp/common";
import { ValidationErrors } from "../../types/common";
import {
  AlphanumericRuleConfig,
  CustomRuleConfig,
  DateRuleConfig,
  EmailRuleConfig,
  FieldValidationConfig,
  FormValidationConfig,
  LengthRuleConfig,
  NumberRangeRuleConfig,
  PhoneRuleConfig,
  RegexRuleConfig,
  UrlVRuleConfig,
  ValidatableValue,
  ValidationResult,
  ValidationRule,
} from "./Form.types";

/* global URL */

const DEFAULT_SPECIAL_CHARS = "-_.'";

export const createEmailRule = (
  config?: EmailRuleConfig,
): ValidationRule<string> => {
  const { customMessage, allowEmpty = false, trim = true } = config || {};

  const validate = (value: string): boolean => {
    const emailValue = trim ? value.trim() : value;

    if (allowEmpty && emailValue === "") {
      return true;
    }

    return emailRegexp.test(emailValue);
  };

  const buildMessage = () => {
    return customMessage || "Invalid email format";
  };

  return {
    validate,
    message: buildMessage(),
  };
};

export const createPhoneRule = (
  config?: PhoneRuleConfig,
): ValidationRule<string> => {
  const {
    countryCode = "US",
    customMessage,
    allowEmpty = false,
    trim = true,
  } = config || {};

  const validate = (value: string): boolean => {
    const phoneValue = trim ? value.trim() : value;

    if (allowEmpty && phoneValue === "") {
      return true;
    }

    const pattern = phoneRegexp[countryCode];
    if (!pattern) {
      return false;
    }

    return pattern.test(phoneValue);
  };

  const buildMessage = () => {
    return customMessage || `Invalid phone number format for ${countryCode}`;
  };

  return {
    validate,
    message: buildMessage(),
  };
};

export const createUrlRule = (
  config?: UrlVRuleConfig,
): ValidationRule<string> => {
  const {
    requireHttps = false,
    allowedProtocols = [],
    customMessage,
    allowEmpty = false,
    trim = true,
  } = config || {};

  const validate = (value: string): boolean => {
    const urlValue = trim ? value.trim() : value;

    if (allowEmpty && urlValue === "") {
      return true;
    }

    try {
      const url = new URL(urlValue);

      if (requireHttps && url.protocol !== "https:") {
        return false;
      }

      if (
        allowedProtocols.length > 0 &&
        !allowedProtocols.includes(url.protocol)
      ) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  const buildMessage = () => {
    if (customMessage) {
      return customMessage;
    }

    const parts = ["Invalid URL"];

    if (requireHttps) {
      parts.push("HTTPS required");
    }

    if (allowedProtocols.length > 0) {
      parts.push(`Allowed protocols: ${allowedProtocols.join(", ")}`);
    }

    return parts.join("\n");
  };

  return {
    validate,
    message: buildMessage(),
  };
};

const isValidDate = (date: Date): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

const isDateInRange = (date: Date, minDate?: Date, maxDate?: Date): boolean => {
  if (minDate && date < minDate) return false;
  if (maxDate && date > maxDate) return false;
  return true;
};

const buildDateValidationMessage = (config: DateRuleConfig): string => {
  if (config.customMessage) return config.customMessage;

  const constraints: string[] = [];

  if (config.minDate) {
    constraints.push(`after ${config.minDate.toLocaleDateString()}`);
  }

  if (config.maxDate) {
    constraints.push(`before ${config.maxDate.toLocaleDateString()}`);
  }

  return constraints.length
    ? `Date must be ${constraints.join(" and ")}`
    : "Invalid date format";
};

export const createDateRule = (
  config?: DateRuleConfig,
): ValidationRule<Date | null> => {
  const { minDate, maxDate, allowEmpty = false } = config || {};

  const validate = (value: Date | null): boolean => {
    if (allowEmpty && value === null) {
      return true;
    }

    if (!value || !isValidDate(value)) {
      return false;
    }

    return isDateInRange(value, minDate, maxDate);
  };

  return {
    validate,
    message: buildDateValidationMessage(config || {}),
  };
};

export const createAlphanumericRule = (
  config?: AlphanumericRuleConfig,
): ValidationRule<string> => {
  const { allowSpaces, allowedSpecialChars, customMessage, maxLength } =
    config || {};

  const escapeRegExp = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const buildRegExp = () => {
    const specialChars =
      allowedSpecialChars !== undefined
        ? allowedSpecialChars
        : DEFAULT_SPECIAL_CHARS;
    const spaceChar = allowSpaces ? "\\s" : "";
    const specialCharPattern = specialChars ? escapeRegExp(specialChars) : "";
    return new RegExp(
      `^[a-zA-Z0-9${spaceChar}${specialCharPattern}]{1,${maxLength || ""}}$`,
    );
  };

  const buildMessage = () => {
    if (customMessage) return customMessage;

    const parts = ["Only letters, numbers"];
    if (allowSpaces) parts.push("spaces");

    const specialChars =
      allowedSpecialChars !== undefined
        ? allowedSpecialChars
        : DEFAULT_SPECIAL_CHARS;

    if (specialChars.length) {
      parts.push(`and these special characters: ${specialChars}`);
    }

    if (maxLength) {
      parts.push(`up to ${maxLength} characters`);
    }

    return parts.join(", ") + " are allowed.";
  };

  return {
    validate: (value: string) => buildRegExp().test(value),
    message: buildMessage(),
  };
};

export const createRegexRule = (
  config: RegexRuleConfig,
): ValidationRule<string> => {
  const {
    pattern,
    customMessage,
    trim = true,
    allowEmpty = false,
    flags,
  } = config;

  const validate = (value: string): boolean => {
    const textValue = trim ? value.trim() : value;

    if (allowEmpty && textValue === "") {
      return true;
    }

    const regexPattern = flags ? new RegExp(pattern.source, flags) : pattern;

    return regexPattern.test(textValue);
  };

  return {
    validate,
    message: customMessage || "Value does not match the required pattern",
  };
};

export const createCustomRule = <T extends ValidatableValue>(
  config: CustomRuleConfig<T>,
): ValidationRule<T> => {
  const {
    validator,
    customMessage,
    allowNull = false,
    allowUndefined = false,
  } = config;

  const validate = (value: T): boolean => {
    if (value === null) return allowNull;
    if (value === undefined) return allowUndefined;

    try {
      return validator(value);
    } catch (error) {
      return false;
    }
  };

  return {
    validate,
    message: customMessage || "Invalid value",
  };
};

export const createLengthRule = (
  config: LengthRuleConfig,
): ValidationRule<string> => {
  const {
    min = 0,
    max,
    customMessage,
    trim = true,
    allowEmpty = false,
  } = config;

  if (max !== undefined && min > max) {
    throw new Error("Minimum length cannot be greater than maximum length");
  }

  const validate = (value: string): boolean => {
    const textValue = trim ? value.trim() : value;

    if (allowEmpty && textValue === "") {
      return true;
    }

    const length = textValue.length;
    if (max !== undefined) {
      return length >= min && length <= max;
    }

    return length >= min;
  };

  const buildMessage = () => {
    if (customMessage) return customMessage;

    if (max !== undefined) {
      return min === max
        ? `Must be exactly ${min} characters`
        : `Must be between ${min} and ${max} characters`;
    }

    return min > 0 ? `Must be at least ${min} characters` : "Invalid length";
  };

  return {
    validate,
    message: buildMessage(),
  };
};

export const createNumberRangeRule = (
  config: NumberRangeRuleConfig,
): ValidationRule<number | null> => {
  const {
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    customMessage,
    allowNull = false,
    precision,
    includeMin = true,
    includeMax = true,
  } = config;

  if (min > max) {
    throw new Error("Minimum value cannot be greater than maximum value");
  }

  const roundToPrecision = (value: number): number => {
    if (precision === undefined) return value;
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  };

  const validate = (value: number | null): boolean => {
    if (value === null) return allowNull;

    const roundedValue = roundToPrecision(value);
    const minValid = includeMin ? roundedValue >= min : roundedValue > min;
    const maxValid = includeMax ? roundedValue <= max : roundedValue < max;

    return minValid && maxValid;
  };

  const buildMessage = () => {
    if (customMessage) return customMessage;

    const minSymbol = includeMin ? "≥" : ">";
    const maxSymbol = includeMax ? "≤" : "<";

    if (min === Number.MIN_SAFE_INTEGER && max === Number.MAX_SAFE_INTEGER) {
      return "Invalid number";
    }

    if (min === max) {
      return `Value must be exactly ${min}`;
    }

    if (min === Number.MIN_SAFE_INTEGER) {
      return `Value must be ${maxSymbol} ${max}`;
    }

    if (max === Number.MAX_SAFE_INTEGER) {
      return `Value must be ${minSymbol} ${min}`;
    }

    return `Value must be ${minSymbol} ${min} and ${maxSymbol} ${max}`;
  };

  return {
    validate,
    message: buildMessage(),
  };
};

export const validateField = <T extends ValidatableValue>(
  value: T,
  config: FieldValidationConfig<T>,
): ValidationResult => {
  if (
    config.required &&
    (value === null || value === undefined || value === "")
  ) {
    return {
      isValid: false,
      error: config.requiredMessage || "This field is required",
    };
  }

  if (!value && !config.required) {
    return { isValid: true, error: null };
  }

  if (config.rules) {
    for (const rule of config.rules) {
      if (!rule.validate(value)) {
        return {
          isValid: false,
          error: rule.message,
        };
      }
    }
  }

  return { isValid: true, error: null };
};

export const validateForm = <T extends Record<string, ValidatableValue>>(
  formData: T,
  config: FormValidationConfig<T>,
): ValidationErrors => {
  const errors: ValidationErrors = {};

  Object.keys(config).forEach((field) => {
    const fieldConfig = config[field];
    const value = formData[field];

    if (fieldConfig) {
      const result = validateField(value, fieldConfig);
      if (!result.isValid && result.error) {
        errors[field] = result.error;
      }
    }
  });

  return errors;
};

export const isFormValid = (errors: ValidationErrors): boolean => {
  return Object.keys(errors).length === 0;
};
